import React from "react";
import { Star } from "lucide-react";

import { cn } from "@/lib/utils";
import type { Localizer } from "@/main/react/Types";

export type ReviewHeaderProps = {
    localizer: Localizer;
    subjectName: string;
    subjectSlug?: string;
    /** Integer half-stars, 1..10. */
    ratingHalfStars: number;
    className?: string;
};

/**
 * The strip above a review post's body: what it is reviewing, and the author's rating.
 *
 * <p>The subject name LINKS to {@code /s/{slug}} and that link is the feature's whole discovery loop —
 * it is how a reader who arrived at one review finds every other review of the same thing. Without a
 * slug there is no page to point at yet, so the name renders as plain text rather than as a dead link.
 *
 * <p>Design language (AGENTS.md rule 23): a border rather than a shadow, and the shared focus ring.
 *
 * <p>Rendered as a SIBLING of the post's other server-rendered content, never nested inside another
 * {@code react-component} container — {@code createRoot()} discards a mount point's children, so a
 * nested island is destroyed by its parent mounting and presents as flakiness rather than absence
 * (rule 32).
 */
export default function ReviewHeader({
    localizer,
    subjectName,
    subjectSlug,
    ratingHalfStars,
    className,
}: ReviewHeaderProps) {
    const name = subjectName?.trim();
    if (!name || !ratingHalfStars) {
        return null;
    }

    const stars = ratingHalfStars / 2;
    const href = subjectSlug?.trim() ? `/s/${encodeURIComponent(subjectSlug.trim())}` : null;

    return (
        <div
            className={cn(
                "mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl border border-border/60 px-3 py-2 text-sm",
                className,
            )}
            data-playwright-id="discussion-review-header"
        >
            <span className="text-muted-foreground">{localizer("discussion.facet.review.header.reviewing")}</span>

            {href ? (
                <a
                    href={href}
                    className="standard-link font-medium text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 rounded-sm"
                    data-playwright-id="discussion-review-header-subject"
                >
                    {name}
                </a>
            ) : (
                <span className="font-medium text-foreground" data-playwright-id="discussion-review-header-subject">
                    {name}
                </span>
            )}

            <span
                className="inline-flex items-center gap-1 text-muted-foreground"
                data-playwright-id="discussion-review-header-rating"
                // One label for the whole rating: five separate star glyphs would be read out as five
                // things when the reader is being told one number.
                aria-label={localizer("discussion.facet.review.rating.value", stars)}
            >
                <span className="inline-flex" aria-hidden="true">
                    {Array.from({ length: 5 }, (_unused, index) => {
                        const fraction = Math.min(1, Math.max(0, stars - index));
                        return (
                            <span key={index} className="relative inline-flex size-4 items-center justify-center">
                                <Star className="size-4 text-muted-foreground/50" />
                                {fraction > 0 ? (
                                    <span
                                        className="absolute inset-0 flex items-center justify-center overflow-hidden"
                                        style={{ clipPath: `inset(0 ${(1 - fraction) * 100}% 0 0)` }}
                                    >
                                        <Star className="size-4 fill-primary text-primary" />
                                    </span>
                                ) : null}
                            </span>
                        );
                    })}
                </span>
                <span aria-hidden="true">{stars}</span>
            </span>
        </div>
    );
}
